export function formatStudentPortalId(student) {
  const strRaw =
    student?.roll_number_string ?? student?.rollNumberString ?? null;
  const str = strRaw != null ? String(strRaw).trim() : "";
  if (str) return str.toUpperCase();
  const n = student?.roll_number ?? student?.rollNumber;
  if (n != null && n !== "") return `xsl${String(n)}`.toUpperCase();
  return "";
}

/**
 * Prefer snake_case API fields; fall back to Prisma/camelCase from member_init.
 * @param {object|null|undefined} obj
 * @param {string|null|undefined} [fallback]
 */
export function resolveLessonBook(obj, fallback) {
  if (!obj || typeof obj !== "object") return fallback;
  const value = obj.lesson_book ?? obj.lessonBook;
  return value != null && value !== "" ? value : fallback;
}

/**
 * Normalize mixed snake/camel student (or report) fields onto snake_case keys.
 * Safe to call on already-normalized objects.
 */
export function normalizeStudentApiFields(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const lesson_book = resolveLessonBook(obj);
  const stay_at = obj.stay_at ?? obj.stayAt;
  const admission_date = obj.admission_date ?? obj.admissionDate;
  const room_number = obj.room_number ?? obj.roomNumber;
  const roll_number = obj.roll_number ?? obj.rollNumber;
  const roll_number_string = obj.roll_number_string ?? obj.rollNumberString;
  const id_document_type = obj.id_document_type ?? obj.idDocumentType;
  const birth_date = obj.birth_date ?? obj.birthDate;
  return {
    ...obj,
    ...(lesson_book != null ? { lesson_book } : {}),
    ...(stay_at != null ? { stay_at } : {}),
    ...(admission_date != null ? { admission_date } : {}),
    ...(room_number != null ? { room_number } : {}),
    ...(roll_number != null ? { roll_number } : {}),
    ...(roll_number_string != null ? { roll_number_string } : {}),
    ...(id_document_type != null ? { id_document_type } : {}),
    ...(birth_date != null ? { birth_date } : {}),
  };
}

/** Student ID with DB login email, e.g. HNI250(hni250@rabbaniquranschool.com). */
export function formatStudentIdWithEmail(student) {
  const id = formatStudentPortalId(student);
  const email = student?.email != null ? String(student.email).trim().toLowerCase() : "";
  if (!id) return email || "";
  if (!email) return id;
  return `${id}(${email})`;
}

export function getGuardianNameFromStudent(student) {
  if (!student) return "";
  if (student.guardian_name) {
    const raw = String(student.guardian_name).trim();
    if (raw.includes("(")) return raw.split("(")[0].trim();
    return raw;
  }
  return student.client?.name ? String(student.client.name).trim() : "";
}

export function formatGuardianDisplay(student) {
  if (!student) return "";
  if (student.guardian_name) return String(student.guardian_name).trim();
  if (student.client?.name) {
    const email = student.client.email ? String(student.client.email).trim() : "";
    return email ? `${student.client.name}(${email})` : student.client.name;
  }
  return "";
}

export function formatGuardianPhone(student) {
  if (!student) return "";
  const raw =
    student.guardian_phone ??
    student.client?.phone ??
    null;
  if (raw == null || raw === "" || raw === 0) return "";
  return String(raw);
}

export function formatGuardianAddress(student) {
  if (!student) return "";
  const raw =
    student.guardian_address ??
    student.client?.address ??
    null;
  if (raw == null) return "";
  return String(raw).trim();
}

export function formatStudentListLabel(student, { includePortalId = true } = {}) {
  const name = student?.name || "";
  if (!name) return "";

  const parts = [];
  if (includePortalId) {
    const portalId = formatStudentPortalId(student);
    parts.push(portalId ? `${name} (${portalId})` : name);
  } else {
    parts.push(name);
  }

  const guardianName = getGuardianNameFromStudent(student);
  if (guardianName) {
    parts.push(`— ${guardianName}`);
  }

  return parts.join(" ");
}

export function resolveStudentMemberId(student) {
  if (!student || typeof student !== "object") return "";
  const id =
    student._id ?? student.member ?? student.memberId ?? student.id ?? "";
  return String(id).trim();
}

export function formatStudentNameWithGuardian(studentOrReport) {
  const student = studentOrReport?.student ?? studentOrReport;
  const name = student?.name || "";
  if (!name) return "";
  const guardianName = getGuardianNameFromStudent(student);
  return guardianName ? `${name} (${guardianName})` : name;
}
